// SUB-OFFICE 7 — FORM 4-T, TERMINAL SUPPORT ROUNDS. Percussive maintenance on
// a unit that remembers. Pull and reseat a cable, hold a knob to its hidden
// setpoint, land a slam inside the sanctioned window, terminate the kill-cards
// in the printed order. The green phosphor editorializes as you work. The
// client records ONLY raw inputs (pulls, seats, hold spans, slam holds, taps);
// the server replays knob stability, slam force, and kill order, and runs the
// satisfaction survey. Correctness is never client-asserted.

import { KILL_RULES_4T, type FixKind4T, type Form4TSpec } from '../../shared/physspec';
import { holdSlamButton, printLayer } from '../ui/chrome';
import { PhysLogRecorder } from './log';
import type { BenchDeps } from './bench';
import type { BehaviorLog } from '../../shared/physlog';

const KIND_TITLE: Record<FixKind4T, string> = {
  cable: 'RESEAT THE CABLE', knob: 'HOLD THE KNOB TO SETPOINT', slam: 'PERCUSSIVE CORRECTION', kill: 'TERMINATE THE JOBS',
};

export function runTerminals(
  deps: BenchDeps,
  taskId: string,
  spec: Form4TSpec
): Promise<BehaviorLog | null> {
  return new Promise((resolve) => {
    const rec = new PhysLogRecorder('FORM4T', taskId);
    const layer = printLayer();

    const card = document.createElement('div');
    card.className = 'paper-modal wide';
    card.innerHTML =
      `<div class="paper-head letterhead"><span>FORM 4-T — TERMINAL SUPPORT · UNIT ${spec.unit_id}</span>` +
      `<span class="tab-num" id="tt-step">FIX 1 / ${spec.fixes.length}</span></div>`;
    const body = document.createElement('div');
    body.className = 'paper-body typewriter';
    card.appendChild(body);

    // ---- the green-phosphor screen -----------------------------------------
    const screen = document.createElement('div');
    screen.style.cssText = 'background:#07120a;color:#43E86C;border:1px solid #1c3a24;padding:10px 12px;font-family:ui-monospace,monospace;font-size:12px;line-height:1.6;min-height:66px;margin-bottom:12px;text-shadow:0 0 4px rgba(67,232,108,.5)';
    body.appendChild(screen);
    const say = (t: string): void => { screen.textContent = t; };

    const stage = document.createElement('div');
    stage.style.cssText = 'min-height:120px';
    body.appendChild(stage);

    const foot = document.createElement('div');
    foot.className = 'paper-foot';
    const abandonBtn = document.createElement('button');
    abandonBtn.type = 'button';
    abandonBtn.className = 'slam-btn secondary';
    abandonBtn.textContent = 'ABANDON THE UNIT';
    foot.appendChild(abandonBtn);
    card.appendChild(foot);
    layer.appendChild(card);

    const stepEl = card.querySelector('#tt-step') as HTMLElement;
    let over = false;
    let i = 0;

    const finishOk = (): void => {
      if (over) return;
      over = true;
      window.removeEventListener('keydown', onKey);
      rec.ev('ticket_closed', { unit: spec.unit_id, recurring: spec.uncloseable ? 1 : 0 });
      card.remove();
      const built = rec.build(false);
      if (built.valid) resolve(built.log);
      else {
        deps.bus.emit('toast', `The service log did not assemble (${built.reason}).`);
        resolve(null);
      }
    };
    const abandon = (): void => {
      if (over) return;
      over = true;
      window.removeEventListener('keydown', onKey);
      rec.ev('abandoned', { unit: spec.unit_id });
      card.remove();
      resolve(null);
    };
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') abandon(); };
    window.addEventListener('keydown', onKey);
    abandonBtn.addEventListener('click', abandon);

    const nextStep = (): void => {
      if (over) return;
      if (i >= spec.fixes.length) {
        say(spec.uncloseable
          ? `UNIT ${spec.unit_id}: RESOLVED (RECURRING). WE BOTH KNOW I WILL BE BACK.`
          : `UNIT ${spec.unit_id}: TICKET CLOSED. YOU MAY SIGN THE SURVEY THE UNIT DID NOT ASK FOR.`);
        window.setTimeout(finishOk, 700);
        return;
      }
      stepEl.textContent = `FIX ${i + 1} / ${spec.fixes.length}`;
      const fix = spec.fixes[i];
      rec.ev('fix_started', { kind: fix.kind });
      say(`${KIND_TITLE[fix.kind]}.`);
      stage.innerHTML = '';
      if (fix.kind === 'cable') renderCable(fix.params);
      else if (fix.kind === 'knob') renderKnob(fix.params);
      else if (fix.kind === 'slam') renderSlam(fix.params);
      else renderKill();
    };

    const advance = (): void => { i++; window.setTimeout(nextStep, 260); };

    // ---- CABLE: pull, then seat into a socket other than socket_from --------
    function renderCable(params: Record<string, number>): void {
      let pulled = false;
      const pull = document.createElement('button');
      pull.type = 'button';
      pull.className = 'slam-btn';
      pull.textContent = 'PULL THE CABLE';
      const sockets = document.createElement('div');
      sockets.style.cssText = 'display:flex;gap:10px;justify-content:center;margin-top:14px';
      const from = params.socket_from ?? 0;
      for (let s = 0; s < 3; s++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'slam-btn secondary';
        b.style.cssText = 'min-width:80px';
        b.textContent = `SOCKET ${s}${s === from ? ' (WAS)' : ''}`;
        b.addEventListener('click', () => {
          if (!pulled) { say('SEAT NOTHING. THE CABLE IS STILL IN. PULL IT FIRST.'); return; }
          rec.ev('cable_seated', { socket: s });
          deps.bus.emit('audio:thunk');
          say(s === from ? 'BACK WHERE IT WAS. BOLD. THE UNIT NOTICES.' : 'SEATED. THE LIGHT STOPS BLINKING. FOR NOW.');
          advance();
        });
        sockets.appendChild(b);
      }
      pull.addEventListener('click', () => {
        pulled = true;
        rec.ev('cable_pulled', {});
        deps.bus.emit('audio:tick');
        say('CABLE OUT. THE UNIT IS BLIND. RESEAT IT SOMEWHERE THAT IS NOT WHERE IT WAS.');
        pull.disabled = true;
      });
      stage.append(pull, sockets);
    }

    // ---- KNOB: accumulate hold toward the hidden setpoint -------------------
    function renderKnob(params: Record<string, number>): void {
      const meter = document.createElement('div');
      meter.style.cssText = 'height:14px;border:1px solid var(--carbon);margin:14px 0;position:relative;overflow:hidden';
      const fill = document.createElement('div');
      fill.style.cssText = 'position:absolute;inset:0 100% 0 0;background:var(--carbon)';
      meter.appendChild(fill);
      const falsePlateau = params.false_plateau === 1;
      let total = 0;
      let holdStart = 0;
      let raf = 0;
      const draw = (): void => {
        raf = requestAnimationFrame(draw);
        const live = holdStart ? rec.now() - holdStart : 0;
        // display saturates near a plausible ceiling; grade-4 fakes a plateau
        const shown = total + live;
        const pct = Math.min(100, (shown / 4000) * 100 * (falsePlateau && shown > 2200 ? 0.6 : 1));
        fill.style.right = `${100 - pct}%`;
      };
      draw();
      const knob = document.createElement('button');
      knob.type = 'button';
      knob.className = 'slam-btn';
      knob.textContent = 'HOLD TO TURN';
      knob.style.touchAction = 'none';
      knob.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        knob.setPointerCapture(e.pointerId);
        holdStart = rec.now();
        rec.ev('knob_hold', {});
      });
      const release = (): void => {
        if (!holdStart) return;
        total += rec.now() - holdStart;
        holdStart = 0;
        rec.ev('knob_release', {});
      };
      knob.addEventListener('pointerup', release);
      knob.addEventListener('pointercancel', release);
      const done = document.createElement('button');
      done.type = 'button';
      done.className = 'slam-btn secondary';
      done.textContent = 'RELEASE THE UNIT';
      done.addEventListener('click', () => {
        release();
        cancelAnimationFrame(raf);
        say('SETPOINT LOGGED. THE BACK OFFICE KNOWS WHERE IT SHOULD HAVE BEEN.');
        advance();
      });
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:10px;justify-content:center';
      row.append(knob, done);
      stage.append(meter, row);
    }

    // ---- SLAM: land one blow in the sanctioned window -----------------------
    function renderSlam(_params: Record<string, number>): void {
      const pad = holdSlamButton('STRIKE THE UNIT', (r) => {
        rec.ev('slam', { hold_ms: Math.round(r.heldMs) });
        deps.bus.emit('audio:thunk');
        say('STRUCK. THE 14TH BLOW OF THE SHIFT. IT REMEMBERS THE OTHER THIRTEEN.');
        advance();
      }, { className: 'red' });
      const wrap = document.createElement('div');
      wrap.style.cssText = 'display:flex;justify-content:center;margin-top:18px';
      wrap.appendChild(pad);
      stage.appendChild(wrap);
    }

    // ---- KILL: terminate the cards in the printed order ---------------------
    function renderKill(): void {
      const ruleText = KILL_RULES_4T.find((r) => spec.kill_rule && r.key === spec.kill_rule.key && r.dir === spec.kill_rule.dir)?.text
        || 'TERMINATE AS YOU SEE FIT.';
      say(ruleText);
      const grid = document.createElement('div');
      grid.style.cssText = 'display:flex;flex-wrap:wrap;gap:10px;justify-content:center;margin-top:14px';
      const remaining = new Set(spec.kill_cards.map((c) => c.id));
      spec.kill_cards.forEach((c) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'slam-btn secondary';
        b.style.cssText = 'font-size:10px;min-width:120px;text-align:left;line-height:1.5';
        b.innerHTML = `JOB #${c.id}<br>IMPUDENCE ${c.impudence} · AGE ${c.age} · SIZE ${c.size}`;
        b.addEventListener('click', () => {
          if (!remaining.has(c.id)) return;
          remaining.delete(c.id);
          rec.ev('kill_tap', { card: c.id });
          deps.bus.emit('audio:buzz');
          b.disabled = true;
          b.style.opacity = '.4';
          b.style.textDecoration = 'line-through';
          if (!remaining.size) {
            say('ALL JOBS TERMINATED. THE QUEUE IS QUIET. THE QUIET IS ALSO A COMPLAINT.');
            advance();
          }
        });
        grid.appendChild(b);
      });
      stage.appendChild(grid);
    }

    rec.ev('ticket_opened', { unit: spec.unit_id });
    deps.bus.emit('chyron', `UNIT ${spec.unit_id} HAS FILED A GRIEVANCE. TERMINAL SUPPORT ROUNDS BEGIN.`);
    nextStep();
  });
}
